import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';

import { AuditLogApi } from '@/api/api.audit.logs';
import { DataGrid } from '@/components';
import { auditLogsDataGridCols } from '@/components/AuditLogs/data';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { formatSortParam } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import { useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

interface AuditLogApiParams {
  page: number;
  limit: number;
  filter?: string;
  sort: string;
  search?: string;
}

const AuditLogsDataGrid = () => {
  const { updateToast } = useToastStore();
  const navigate = useNavigate();
  const { user } = useUserStore();

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `audit-logs-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return auditLogsDataGridCols;
      }

      return mergeColumnSettings(auditLogsDataGridCols, savedSettings);
    },
  );

  const fetchAuditLogs = useCallback(
    async (
      page = 1,
      limit = 100,
      filter?: string,
      sort?: string,
    ): Promise<{ rows: any[]; totalCount: number }> => {
      try {
        let searchQuery;
        if (filter) {
          const parsedFilter = JSON.parse(filter);
          searchQuery = parsedFilter?.quickFilterValues?.join(' ');
        }

        const apiParams = {
          page,
          limit,
          filter,
          sort: formatSortParam(sort, { createdAt: -1 }),
        } as AuditLogApiParams;

        if (searchQuery) {
          apiParams.search = searchQuery;
        }

        const response = await AuditLogApi.getAuditLogs(apiParams);
        return {
          rows: response.data.docs,
          totalCount: response.data.totalDocs,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [updateToast],
  );

  const columns = useDataGridColumns({
    type: 'auditLogs',
    columnSettings,
    navigate,
  });

  return (
    <Box sx={{ height: '80vh', overflowY: 'auto' }}>
      <DataGrid
        key="audit"
        name="audit-logs"
        fetchData={fetchAuditLogs}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        columns={columns}
        hideFilter
        autoHeight={false}
      />
    </Box>
  );
};

export default AuditLogsDataGrid;
